/**
 * WorkbenchHostContext — the capabilities spine (F0 of the composable-extensions
 * plan, docs/2026-07-24-composable-extensions.md §spine).
 *
 * v1 contributions are pure render (Layer/Section/node). The new actionable
 * seams (commands, menus, context-menus) must *act* — open a tab, change
 * selection, run a mutation — so the host hands every actionable contribution
 * ONE capabilities object as a call argument (`run(ctx)`/`items(ctx)`/
 * `when(ctx)`), never a hook inside a pure view (same purity discipline as v1's
 * `pluginSections` prop threading).
 *
 * The subtle part is splitting ACTION IDENTITY from READ FRESHNESS:
 *
 *  - The `ctx` object is built ONCE (`makeHostContext`). Its action closures
 *    (`openTab`, `select`, `runCommand`, `toast`, …) are stable references, so
 *    passing `ctx` never churns a memoized consumer.
 *  - Its read members are LIVE GETTERS that read current store state at CALL
 *    time (`model()`, `openTabs()`, and `selection`/`activeRepo` as getter
 *    properties, not a captured snapshot). A frozen snapshot would make
 *    `when(ctx)` gate on a stale selection → a palette showing the wrong
 *    commands. Both delegate through a mutable `capabilities` ref the host
 *    updates each render, so identity stays stable while reads stay fresh.
 *
 * Absent capability (the viewer has no repo/mutations) → the action is a NOOP
 * and the guarded contribution self-hides via `when(ctx)`.
 */
import { createContext, useContext } from "react";
import type { ArchitectureModel } from "@archlang/scene/arch-model";
import type { DetailSelection } from "./types.js";

/** The repo an actionable contribution acts against — a slug/id pair in Studio,
 *  null in the viewer (no server-backed repo). Kept minimal here; later phases
 *  widen it as seams consume more. */
export interface RepoRef {
  readonly slug: string;
  readonly repo: string;
}

/** A currently-open main-panel tab, as seen by a contribution. */
export interface TabRef {
  readonly id: string;
  readonly kind: string;
}

/** Where to open a tab — a built-in kind or (later) a plugin `panelKind`. F0
 *  only names it; F2 gives it the full custom-tab contract. */
export interface OpenTabSpec {
  readonly kind: string;
  readonly [key: string]: unknown;
}

/** A nav target to reveal + scroll to in the left rail. */
export interface NavRef {
  readonly path?: string;
  readonly moduleId?: string;
}

/** A transient toast notification. */
export interface ToastSpec {
  readonly message: string;
  readonly tone?: "info" | "success" | "warn" | "error";
}

/**
 * The one capabilities surface the host provides. Read members are live (read
 * store state at call time); act members delegate to the store/composition and
 * are NOOP when the host doesn't support them.
 */
export interface WorkbenchHostContext {
  // ---- read (live getters; never a frozen snapshot the plugin must diff)
  /** slug/id when in Studio, null in the viewer. */
  readonly activeRepo: RepoRef | null;
  /** What the details panel currently has selected, or null. */
  readonly selection: DetailSelection | null;
  /** The compiled model, read lazily at call time. */
  readonly model: () => ArchitectureModel | null;
  /** The currently-open tabs, in order. */
  readonly openTabs: () => readonly TabRef[];
  // ---- act (delegate to the store / composition; NOOP when unsupported)
  openTab(spec: OpenTabSpec): void;
  select(sel: DetailSelection): void;
  revealInNav(ref: NavRef): void;
  runCommand(commandId: string): void;
  toast(msg: ToastSpec): void;
}

/**
 * What a composition supplies to build the context — the live-read sources +
 * the action implementations, all owned by the store/composition already. Every
 * member is optional: an absent read yields null/empty, an absent action is a
 * NOOP (the viewer supplies none → a fully-NOOP context).
 */
export interface HostCapabilities {
  readonly activeRepo?: () => RepoRef | null;
  readonly selection?: () => DetailSelection | null;
  readonly model?: () => ArchitectureModel | null;
  readonly openTabs?: () => readonly TabRef[];
  readonly openTab?: (spec: OpenTabSpec) => void;
  readonly select?: (sel: DetailSelection) => void;
  readonly revealInNav?: (ref: NavRef) => void;
  readonly runCommand?: (commandId: string) => void;
  readonly toast?: (msg: ToastSpec) => void;
}

const EMPTY_TABS: readonly TabRef[] = Object.freeze([]);

/**
 * Build the ctx ONCE against a mutable capabilities ref. Every closure and
 * getter reads `ref.current` at call time, so:
 *  - the returned object's identity + action refs are stable across renders
 *    (build it once, e.g. `useMemo(() => makeHostContext(ref), [])`);
 *  - reads reflect the LATEST capabilities the host wrote to the ref.
 * Pass a plain `{ current }` box (a React ref, or `{ current: caps }` in tests).
 */
export function makeHostContext(ref: { current: HostCapabilities }): WorkbenchHostContext {
  return {
    get activeRepo() {
      return ref.current.activeRepo?.() ?? null;
    },
    get selection() {
      return ref.current.selection?.() ?? null;
    },
    model: () => ref.current.model?.() ?? null,
    openTabs: () => ref.current.openTabs?.() ?? EMPTY_TABS,
    openTab: (spec) => ref.current.openTab?.(spec),
    select: (sel) => ref.current.select?.(sel),
    revealInNav: (r) => ref.current.revealInNav?.(r),
    runCommand: (id) => ref.current.runCommand?.(id),
    toast: (msg) => ref.current.toast?.(msg),
  };
}

/** The viewer default: every read empty, every action a NOOP. Shared frozen
 *  instance so a workbench mounted without a host reads a stable ctx. */
export const NOOP_HOST_CONTEXT: WorkbenchHostContext = makeHostContext({ current: {} });

const Ctx = createContext<WorkbenchHostContext>(NOOP_HOST_CONTEXT);
export const WorkbenchHostContextProvider = Ctx.Provider;

/** Read the host capabilities. Defaults to the fully-NOOP context (viewer), so
 *  a contribution's `when(ctx)`/`run(ctx)` is always callable — absent
 *  capabilities just make it hide/do-nothing, never throw. */
export function useWorkbenchHost(): WorkbenchHostContext {
  return useContext(Ctx);
}

/**
 * The shared `{ when, run }` contract every actionable seam (commands, menus,
 * context-menu items) resolves against `ctx`. `when` is a cheap predicate the
 * host re-evaluates at the moments that matter (palette open, selection change,
 * active-repo change) — not a subscription; nothing diffs.
 */
export interface Actionable {
  readonly when?: (ctx: WorkbenchHostContext) => boolean;
  readonly run: (ctx: WorkbenchHostContext) => void;
}

/** Is this contribution visible in the current context? A missing `when` is
 *  always-visible; guarding on an absent capability hides it. */
export function isActionable(item: { readonly when?: (ctx: WorkbenchHostContext) => boolean }, ctx: WorkbenchHostContext): boolean {
  return item.when ? item.when(ctx) : true;
}

/** Run an actionable against ctx IF its `when` gate passes; returns whether it
 *  ran. The single resolver all three actionable seams call, so gate + act
 *  semantics never drift between them. */
export function runActionable(item: Actionable, ctx: WorkbenchHostContext): boolean {
  if (!isActionable(item, ctx)) return false;
  item.run(ctx);
  return true;
}
